import React from "react";
import { Link } from "react-router-dom";
import { NumericFormat as NumberFormat } from "react-number-format";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { productoActions } from "../../../_actions";
import { BadgeOferta, PrecioTachado, precioVigente } from "./Oferta";
import imageService from "../../../_services/cart/image.service";
import productoServiceTs from "../../../_services/cart/producto.service";

/*
 * Fila deslizable de publicaciones: "Bajas destacadas", "Ofertas del momento", "Otros
 * productos del vendedor / de esta baja / similares" y las ultimas de la tienda.
 *
 * Antes era un pure-react-carousel con la fila de puntos y dos flechas del tamanio de un
 * boton de accion DEBAJO de las cards, y cada card apilaba tipo, marca, modelo y version en
 * cuatro renglones: el bloque rondaba los 500px de alto para mostrar dos fotos. Con pocas
 * publicaciones, ademas, `visibleSlides` se achicaba a la cantidad de productos y dos cards
 * se estiraban a medio ancho de pantalla cada una.
 *
 * Ahora es scroll horizontal nativo con scroll-snap:
 *  - el ancho de la card lo fija el CSS por breakpoint, no la cantidad de productos;
 *  - en mobile se desliza con el dedo y asoma la card siguiente como pista de que hay mas;
 *  - en desktop las flechas van chicas y encima de las fotos, y solo aparecen si hay hacia
 *    donde moverse.
 *
 * `plano`: sin caja propia, para cuando el contenedor ya pone la suya (la tienda).
 */
class CarrouselProductosRel extends React.Component {
  state = {
    appName: process.env.PUBLIC_URL,
    puedeAnterior: false,
    puedeSiguiente: false,
  };

  track = React.createRef();

  componentDidMount() {
    this.actualizarFlechas();
    window.addEventListener("resize", this.actualizarFlechas);
  }

  componentDidUpdate(prevProps) {
    // En la ficha, al elegir otra publicacion llega otra lista: vuelve al principio.
    if (prevProps.productos !== this.props.productos) {
      if (this.track.current) this.track.current.scrollLeft = 0;
      this.actualizarFlechas();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.actualizarFlechas);
  }

  actualizarFlechas = () => {
    const el = this.track.current;
    if (!el) return;
    // Tolerancia de 2px: con zoom o anchos fraccionarios scrollLeft nunca llega justo al final.
    const puedeAnterior = el.scrollLeft > 2;
    const puedeSiguiente = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    if (
      puedeAnterior !== this.state.puedeAnterior ||
      puedeSiguiente !== this.state.puedeSiguiente
    ) {
      this.setState({ puedeAnterior, puedeSiguiente });
    }
  };

  desplazar = (sentido) => {
    const el = this.track.current;
    if (!el) return;
    // Casi una pantalla de cards por clic: la ultima visible queda como primera, como referencia.
    el.scrollBy({ left: sentido * el.clientWidth * 0.9, behavior: "smooth" });
  };

  buscarPrimeraImagen = (prod) => {
    if (prod.imagenes && prod.imagenes.length > 0) {
      return imageService.buscarPrimeraImagen(prod.imagenes, this.state.appName);
    }
    if (prod.productoBaja?.imagenes?.length > 0) {
      return imageService.buscarPrimeraImagen(
        prod.productoBaja.imagenes,
        this.state.appName
      );
    }
    return { url: this.state.appName + "/images/banner-bg.jpg", type: "image/jpeg" };
  };

  /*
   * Titulo y bajada de la card, cada uno en UN renglon.
   *
   * En una baja el producto ES el auto, asi que el titulo es marca y modelo, la bajada version
   * y anio, y "Baja" va de etiqueta sobre la foto (en mobile la card mide ~135px y no entraba
   * en el renglon); en un repuesto el titulo es la pieza y el auto entero va abajo, igual que
   * en la card del listado.
   */
  textos = (prod) => {
    const esBaja = prod.tipoRepuesto?.nombre === "Baja";
    const marcaModelo = [prod.marca?.nombre, prod.modelo?.nombre]
      .filter(Boolean)
      .join(" ");

    if (esBaja) {
      return {
        esBaja,
        titulo: marcaModelo || "Baja",
        bajada: [prod.version?.nombre, prod.anio].filter(Boolean).join(" · "),
      };
    }
    return {
      esBaja,
      titulo: prod.tipoRepuesto?.nombre,
      bajada: [
        [marcaModelo, prod.version?.nombre].filter(Boolean).join(" "),
        prod.anio,
      ]
        .filter(Boolean)
        .join(" · "),
    };
  };

  renderPrecio = (prod, esBaja) => {
    // Sin precio no hay numero que mostrar: el DTO lo trae en null y el formateador
    // pintaba un "$" pelado.
    if (productoActions.esPrecioAConsultar(prod)) {
      return <span className="rel-card__precio--consultar">Precio a consultar</span>;
    }
    if (esBaja) return null;
    return (
      <>
        <NumberFormat
          prefix={"$"}
          value={precioVigente(prod)}
          displayType={"text"}
          thousandSeparator="."
          decimalSeparator=","
        />
        <PrecioTachado oferta={prod.oferta} className="rel-card__lista" />
      </>
    );
  };

  render() {
    const { productos, nombre, plano } = this.props;
    const { puedeAnterior, puedeSiguiente } = this.state;

    if (!productos || productos.length === 0) return null;

    return (
      <section className={`rel-carrusel${plano ? " rel-carrusel--plano" : ""}`}>
        {!!nombre && <h2 className="rel-carrusel__titulo">{nombre}</h2>}

        <div className="rel-carrusel__viewport">
          <ul
            className="rel-carrusel__track"
            ref={this.track}
            onScroll={this.actualizarFlechas}
          >
            {productos.map((prod) => {
              const { esBaja, titulo, bajada } = this.textos(prod);
              const precio = this.renderPrecio(prod, esBaja);
              return (
                <li className="rel-carrusel__item" key={prod.id}>
                  <Link
                    className="rel-card"
                    to={productoServiceTs.crearUrlProducto(prod)}
                    onClick={() => {
                      if (this.props.refreshProducto !== undefined) {
                        this.props.refreshProducto(prod.id);
                      }
                    }}
                  >
                    <div className="rel-card__media">
                      <img
                        src={this.buscarPrimeraImagen(prod).url}
                        alt={[titulo, bajada].filter(Boolean).join(" — ")}
                        loading="lazy"
                      />
                      {esBaja ? (
                        <span className="rel-card__tag">Baja</span>
                      ) : (
                        <BadgeOferta oferta={prod.oferta} className="rel-card__badge" />
                      )}
                    </div>
                    <div className="rel-card__body">
                      <h3 className="rel-card__titulo" title={titulo}>
                        {titulo}
                      </h3>
                      {!!bajada && (
                        <p className="rel-card__bajada" title={bajada}>
                          {bajada}
                        </p>
                      )}
                      {!!precio && <p className="rel-card__precio">{precio}</p>}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>

          {puedeAnterior && (
            <button
              type="button"
              className="rel-carrusel__flecha rel-carrusel__flecha--prev"
              aria-label="Anteriores"
              onClick={() => this.desplazar(-1)}
            >
              <FontAwesomeIcon icon="fa-solid fa-chevron-left" />
            </button>
          )}
          {puedeSiguiente && (
            <button
              type="button"
              className="rel-carrusel__flecha rel-carrusel__flecha--next"
              aria-label="Siguientes"
              onClick={() => this.desplazar(1)}
            >
              <FontAwesomeIcon icon="fa-solid fa-chevron-right" />
            </button>
          )}
        </div>
      </section>
    );
  }
}

export default CarrouselProductosRel;
